import React from "react";
import {
  Box,
  Checkbox,
  Typography,
  Chip,
  TextField,
  Tooltip,
  IconButton,
} from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus } from "@fortawesome/free-solid-svg-icons";

export const VolumeBipedRow = React.memo(
  ({
    item,
    isSelected,
    canSelect,
    selectedVolumeName,
    quantityValue,
    handleToggleSelectItem,
    handleQuantityChange,
    handleQuantityKeyDown,
    handleAddSingleItem,
  }) => {
    // Se não pode selecionar (já está alocado), desabilita tudo
    const isDisabled = !canSelect;

    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          px: 2,
          py: 1,
          boxSizing: "border-box",
          borderBottom: "1px solid #f0f0f0",
          backgroundColor: isDisabled ? "#f5f5f5" : "#fff",
          "&:hover": {
            backgroundColor: isDisabled ? "#f5f5f5" : "#f9f9f9",
          },
          opacity: isDisabled ? 0.5 : 1,
        }}>
        {/* Checkbox alinhado - DESABILITADO se já alocado */}
        <Box sx={{ width: 30, display: "flex", justifyContent: "center" }}>
          <Checkbox
            checked={isSelected}
            onChange={() => handleToggleSelectItem(item.id)}
            size="small"
            disabled={isDisabled || !selectedVolumeName}
          />
        </Box>

        {/* Descrição do Item */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="body2"
            fontWeight="bold"
            noWrap
            sx={{
              color: isDisabled ? "text.disabled" : "inherit",
            }}>
            {item.name}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {item.code} • {item.id}
          </Typography>
          {item.type === "product" && item.alreadyAllocated > 0 && (
            <Typography variant="caption" color="warning.main" display="block">
              Já alocado: {item.alreadyAllocated} un
            </Typography>
          )}
          {/* Badge visual para item já alocado */}
          {isDisabled && (
            <Chip
              label="Já alocado"
              size="small"
              color="default"
              sx={{
                mt: 0.5,
                fontSize: "0.7rem",
                backgroundColor: "#e0e0e0",
              }}
            />
          )}
        </Box>

        {/* Coluna Tipo */}
        <Box sx={{ width: 100, display: "flex", justifyContent: "center" }}>
          <Chip
            label={item.type === "equipment" ? "Equipamento" : "Insumo"}
            size="small"
            color={item.type === "equipment" ? "secondary" : "primary"}
            sx={{ width: "100%", opacity: isDisabled ? 0.5 : 1 }}
          />
        </Box>

        {/* Coluna Disponível */}
        <Box sx={{ width: 90, display: "flex", justifyContent: "center" }}>
          <Typography
            variant="body2"
            fontWeight="bold"
            sx={{
              color: isDisabled
                ? "text.disabled"
                : item.type === "product" && item.remaining <= 0
                  ? "error.main"
                  : "inherit",
            }}>
            {isDisabled
              ? "—"
              : item.type === "equipment"
                ? "1"
                : `${item.remaining}/${item.available}`}
          </Typography>
        </Box>

        {/* Coluna Qtd. Input */}
        <Box sx={{ width: 80, display: "flex", justifyContent: "center" }}>
          {item.type === "product" ? (
            <TextField
              size="small"
              type="number"
              placeholder={String(item.remaining)}
              value={quantityValue ?? ""}
              onChange={(e) => handleQuantityChange(item.id, e.target.value)}
              onKeyDown={(e) => handleQuantityKeyDown(e, item)}
              sx={{ width: "100%" }}
              inputProps={{
                min: 1,
                max: item.remaining,
                style: { textAlign: "center", padding: "6px 4px" },
              }}
              disabled={isDisabled}
            />
          ) : (
            <Typography variant="body2" color="text.disabled">
              —
            </Typography>
          )}
        </Box>

        {/* Coluna de Ações Fixa (70px) */}
        <Box
          sx={{
            width: 70,
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-start",
            gap: 0.5,
          }}>
          {(item.type === "product" || item.type === "equipment") && (
            <Tooltip
              title={
                isDisabled
                  ? "Item já alocado em um volume"
                  : "Adicionar ao Volume"
              }
              placement="top">
              <span>
                {" "}
                {/* Wrapper span para tooltip em elemento desabilitado */}
                <IconButton
                  size="small"
                  color="primary"
                  onClick={() => handleAddSingleItem(item)}
                  disabled={isDisabled || !selectedVolumeName}>
                  <FontAwesomeIcon icon={faPlus} />
                </IconButton>
              </span>
            </Tooltip>
          )}

          {item.type === "product" && (
            <Typography variant="caption" color="text.secondary">
              un
            </Typography>
          )}
        </Box>
      </Box>
    );
  },
  (prevProps, nextProps) => {
    return (
      prevProps.item.id === nextProps.item.id &&
      prevProps.isSelected === nextProps.isSelected &&
      prevProps.canSelect === nextProps.canSelect &&
      prevProps.selectedVolumeName === nextProps.selectedVolumeName &&
      prevProps.quantityValue === nextProps.quantityValue
    );
  },
);
